/**
 * Verse analysis entry in the verse menu — a peer menu item at the FOOT
 * of the list. Problem verdicts get a tappable "View <severity>" row that
 * opens the analysis panel; order-only verdicts get a benign note; clean
 * verses render nothing.
 *
 * It reads as an ordinary menu row (same metrics, same hover) with only
 * the severity-coloured icon setting it apart — as a headline card above
 * the list it fought "It is written…" for the eye on every verse opened.
 */

import { observer } from 'mobx-react-lite';
import type { BibleLocation } from '@skjv/common';
import { bibleState } from '@/state/bible-state';
import { CheckCircleIcon } from '@components/icons';
import { severityFor } from '@components/verse-analysis-panel';

interface VerseMenuAnalysisRowProps {
  location: BibleLocation;
}

export const VerseMenuAnalysisRow = observer(function VerseMenuAnalysisRow({
  location,
}: VerseMenuAnalysisRowProps) {
  const verdict = bibleState.verseAnalysisVerdictFor(location);

  if (!verdict || verdict === 'loading' || verdict.verdict === 'none') return null;

  if (verdict.verdict === 'order_only') {
    return (
      <>
        <div className="verse-menu-divider" />
        <div className="verse-menu-analysis-benign analysis-row-appear">
          <CheckCircleIcon />
          <span>Same words, different order — meaning unchanged.</span>
        </div>
      </>
    );
  }

  const severity = severityFor(verdict.score);
  const handleOpen = () => {
    bibleState.showVerseAnalysis(verdict.analysis, location);
    bibleState.hideVerseMenu();
  };

  return (
    <>
      <div className="verse-menu-divider" />
      <button
        className={`verse-menu-item verse-menu-analysis analysis-row-appear ${severity.className}`}
        onClick={handleOpen}
      >
        <severity.Icon className="verse-menu-analysis-icon" filled />
        <span className="verse-menu-analysis-title">View {severity.title}</span>
      </button>
    </>
  );
});
